// full file (overwrite existing)
import React, { useState, useEffect } from "react";
import Step1 from "./Step1";
import ChildrenForm from "./ChildrenForm";
import YouthForm from "./YouthForm";
import CollegeForm from "./CollegeForm";
import AdultForm from "./AdultForm";
import PolicyForm from "./PolicyForm";
import RegistrationComplete from "./RegistrationComplete";
import { registerUser } from "./api";

// Registration opens 2026-05-01 at 10:00 AM Central (America/Chicago, UTC-5 during CDT)
const LAUNCH_TIME = new Date("2026-05-01T10:00:00-05:00");

// Localhost-only bypass (for testing): add ?bypassCountdown=1
const isDevHost =
  typeof window !== "undefined" &&
  (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname.endsWith(".app.github.dev") || // Codespaces forwarded ports
    window.location.hostname.endsWith(".github.dev")        // github.dev editor previews
  );

const bypassCountdown =
  isDevHost && new URLSearchParams(window.location.search).get("bypassCountdown") === "1";

function useCountdown(targetMs) {
  const [timeLeft, setTimeLeft] = useState(() => Math.max(0, targetMs - Date.now()));

  useEffect(() => {
    if (timeLeft <= 0) return;

    const id = setInterval(() => {
      const diff = Math.max(0, targetMs - Date.now());
      setTimeLeft(diff);
      if (diff <= 0) clearInterval(id);
    }, 1000);

    return () => clearInterval(id);
  }, [targetMs, timeLeft]);

  return timeLeft;
}

function RegistrationCountdown() {
  const timeLeft = useCountdown(LAUNCH_TIME.getTime());

  const totalSeconds = Math.floor(timeLeft / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (n) => String(n).padStart(2, "0");

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#f8fafc",
        padding: "24px",
        textAlign: "center",
        fontFamily: "inherit",
      }}
    >
      <h1 style={{ fontSize: 28, fontWeight: 900, margin: "0 0 8px 0", color: "#1e3a5f" }}>
        Camp Fusion Registration
      </h1>
      <p style={{ fontSize: 18, color: "#555", margin: "0 0 40px 0" }}>
        Registration opens May 1, 2026 at 10:00 AM
      </p>

      <div
        style={{
          display: "flex",
          gap: 16,
          flexWrap: "wrap",
          justifyContent: "center",
          marginBottom: 40,
        }}
      >
        {[
          { value: days, label: "Days" },
          { value: hours, label: "Hours" },
          { value: minutes, label: "Minutes" },
          { value: seconds, label: "Seconds" },
        ].map(({ value, label }) => (
          <div
            key={label}
            style={{
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 16,
              padding: "20px 28px",
              minWidth: 90,
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <div style={{ fontSize: 56, fontWeight: 900, lineHeight: 1, color: "#1e3a5f" }}>
              {pad(value)}
            </div>
            <div
              style={{
                fontSize: 13,
                color: "#888",
                marginTop: 6,
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              {label}
            </div>
          </div>
        ))}
      </div>

      <p style={{ fontSize: 14, color: "#aaa" }}>
        Check back soon — the form will open automatically when registration begins.
      </p>

      {isDevHost ? (
        <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 12 }}>
          Local testing tip: add <code>?bypassCountdown=1</code> to the URL to view the form.
        </p>
      ) : null}
    </div>
  );
}

export default function MultiStepForm() {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [step, setStep] = useState("age");
  const [age, setAge] = useState(null);
  const [formData, setFormData] = useState({});
  const [registered, setRegistered] = useState(false);

  // Keep time updated so the countdown check re-evaluates each second
  useEffect(() => {
    if (bypassCountdown) return;
    if (nowMs >= LAUNCH_TIME.getTime()) return;

    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [nowMs]);

  if (!bypassCountdown && nowMs < LAUNCH_TIME.getTime()) {
    return <RegistrationCountdown />;
  }

  const restart = () => {
    setStep("age");
    setAge(null);
    setFormData({});
    setRegistered(false);
  };

  const goToPolicyWithMerge = (data) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setStep("policy");
  };

  if (step === "age") {
    return (
      <Step1
        onAgeSelected={(selectedAge, dob, grade) => {
          console.log("MultiStepForm onAgeSelected received:", { selectedAge, dob, grade });
          setAge(selectedAge);
          setFormData((prev) => ({ ...prev, age: selectedAge, dob, gradeCompleted: grade }));
          setStep("group");
        }}
      />
    );
  }

  const renderGroupStep = () => {
    // Ensure age is numeric or null
    const numericAge = age === null || Number.isNaN(Number(age)) ? null : Number(age);

    // Age 0..12 -> Children form (covers nursery, VBS, middle-school mappings)
    if (numericAge !== null && numericAge >= 0 && numericAge <= 12) {
      // pass age down so ChildrenForm can render the correct question
      return <ChildrenForm age={numericAge} onComplete={goToPolicyWithMerge} />;
    }

    // Age 13..17 -> Youth form
    if (numericAge !== null && numericAge >= 13 && numericAge <= 17) {
      return <YouthForm onComplete={goToPolicyWithMerge} />;
    }

    // Age 18..23 -> College form
    if (numericAge !== null && numericAge >= 18 && numericAge <= 23) {
      return <CollegeForm onComplete={goToPolicyWithMerge} />;
    }

    // Age 24+ -> Adult form
    if (numericAge !== null && numericAge >= 24) {
      return <AdultForm onComplete={goToPolicyWithMerge} />;
    }

    return <div>Sorry, age group not supported.</div>;
  };

  if (step === "group") {
    return renderGroupStep();
  }

  if (step === "policy") {
    return (
      <PolicyForm
        onAgree={async (policyData) => {
          const finalAge =
            typeof formData.age === "number" ? formData.age : age !== null ? Number(age) : null;

          const finalData = { ...formData, age: finalAge, ...policyData };

          console.log("Submitting finalData to registerUser:", finalData);

          try {
            const result = await registerUser(finalData);
            console.log("registerUser result:", result);
            setRegistered(true);
            setStep("complete");
          } catch (err) {
            console.error("registerUser failed:", err);
            alert("Submission failed. Check console for details.");
          }
        }}
      />
    );
  }

  if (step === "complete") {
    return <RegistrationComplete onRestart={restart} registered={registered} />;
  }

  return null;
}